import Quill from "quill";
import type { EmbedBlot, ScrollBlot } from "parchment";

import { parseCrmActionEmbed, type CrmActionEmbedData } from "../utils/crmActionDelta";

const Embed = Quill.import("blots/embed") as typeof EmbedBlot;

export const CHAT_CRM_ACTION_CLICK = "chat-crm-action-click";

class CrmActionBlot extends Embed {
  static blotName = "crm-action";
  static tagName = "span";
  static className = "chat-crm-action";

  private clickHandler?: EventListener;
  private mounted = false;

  constructor(scroll: ScrollBlot, node: Node) {
    super(scroll, node);
  }

  static create(data?: CrmActionEmbedData) {
    const node = super.create() as HTMLElement;
    const parsed = parseCrmActionEmbed(data);
    if (!parsed) {
      return node;
    }

    const button = document.createElement("button");
    button.type = "button";
    button.className = "chat-crm-action-btn";
    button.textContent = parsed.label;
    button.setAttribute("aria-label", parsed.label);
    node.appendChild(button);

    return CrmActionBlot.setDataValues(node, parsed);
  }

  static setDataValues(element: HTMLElement, data: CrmActionEmbedData) {
    element.dataset.actionType = data.type;
    element.dataset.label = data.label;
    if (data.prefill) {
      element.dataset.prefill = JSON.stringify(data.prefill);
    }
    return element;
  }

  static value(domNode: HTMLElement): CrmActionEmbedData {
    let prefill: CrmActionEmbedData["prefill"];
    if (domNode.dataset.prefill) {
      try {
        prefill = JSON.parse(domNode.dataset.prefill) as CrmActionEmbedData["prefill"];
      } catch {
        prefill = undefined;
      }
    }
    return {
      type: (domNode.dataset.actionType ?? "create-task") as CrmActionEmbedData["type"],
      label: domNode.dataset.label ?? domNode.dataset.actionType ?? "Action",
      prefill,
    };
  }

  attach() {
    super.attach();
    if (!this.mounted) {
      this.mounted = true;
      this.clickHandler = (e: Event) => {
        const target = e.target as HTMLElement;
        if (!target.closest(".chat-crm-action-btn")) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        const detail = CrmActionBlot.value(this.domNode as HTMLElement);
        window.dispatchEvent(
          new CustomEvent(CHAT_CRM_ACTION_CLICK, { detail, bubbles: true })
        );
      };
      this.domNode.addEventListener("click", this.clickHandler, false);
    }
  }

  detach() {
    super.detach();
    this.mounted = false;
    if (this.clickHandler) {
      this.domNode.removeEventListener("click", this.clickHandler);
      this.clickHandler = undefined;
    }
  }
}

let registered = false;

export function registerCrmActionBlot(): void {
  if (registered) {
    return;
  }
  Quill.register(CrmActionBlot);
  registered = true;
}

export { CrmActionBlot };
